import React, { FC } from 'react';
import { Button, Drawer, Loader, Tooltip } from 'freemium-ui';
import { History } from 'history';
import { get } from 'lodash';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useGetContactLists } from 'routes/screens/lists/hooks';
import { SentimentType } from 'routes/screens/response/types';
import { useGetTenantInfo } from 'routes/screens/settings/hooks';
import { getDisplayValueFromField } from 'routes/utils';
import EmptyState from 'shared/components/EmptyState';
import Icon from 'shared/components/Icon';
import {
  HIDE_CONTACT_DETAILS_FIELDS,
  SHOW_CONTACT_DETAILS_FIELDS,
  TEMPORARY_HIDDEN_FIELDS,
} from 'shared/constants';
import { useGetContactForm } from 'shared/hooks';
import { Center } from 'shared/styles';
import {
  AccountInfoType,
  ContactListType,
  ContactListsResponseType,
  FormServFieldType,
  McrContactType,
  SurveyResponseType,
} from 'shared/types';
import { DivA11y } from 'utils/a11y-utils';
import { useGetContact } from '../../../../hooks';
import {
  ContactDetailField,
  ContactDetailsTable,
  FixedContainer,
  SentimentTableFooter,
  SentimentTagsTable,
} from './styles';
import RespondedFrom from './RespondedFrom';
import { SentimentEdit } from './sentiment-edit';

type ContactDisplayProps = {
  accountInfo?: AccountInfoType;
  surveyResponse: SurveyResponseType;
};

export const HIDE_FIELDS: string[] = [
  'list_ids',
  'updater_id',
  'creater_id',
  'updated_at',
  'created_at',
];

const SentimentTags: FC<{
  sentimentTags: string[];
  sentiment: SentimentType;
}> = ({ sentimentTags, sentiment }) => {
  const { t } = useTranslation();

  if (!sentimentTags?.length) return <></>;

  return (
    <div className="sentiment-container">
      <div className="sentiment-heading">
        {t(`responses.freddy_sentiment.${sentiment}`)}
      </div>
      <div className="sentiment-tags-container">
        {sentimentTags?.map((tag) => {
          return (
            <div className="sentiment-tags" key={tag}>
              {tag}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ContactDetails: FC<
  ContactDisplayProps & {
    isError: boolean;
    contactFields: FormServFieldType[];
    contactLists: ContactListsResponseType;
    contactData: McrContactType;
    isContactListsError: boolean;
  }
> = ({
  accountInfo,
  surveyResponse,
  isError,
  contactFields: fields,
  contactLists: contactListsResponse,
  contactData,
  isContactListsError,
}) => {
  const { t, i18n } = useTranslation();
  const history: History = useHistory();

  const { allContactLists, contactLists } = React.useMemo<{
    allContactLists: Record<string, ContactListType>;
    contactLists: ContactListType[];
  }>(() => {
    const newLists: Record<string, ContactListType> = {};
    if (!contactListsResponse || !contactListsResponse.contactList)
      return { allContactLists: {}, contactLists: [] };
    contactListsResponse.contactList.forEach((contactList: ContactListType) => {
      if (contactList.display_id)
        newLists[contactList.display_id] = contactList;
    });
    return {
      allContactLists: newLists,
      contactLists: contactListsResponse.contactList || [],
    };
  }, [contactListsResponse]);

  if (isError) {
    return (
      <Center>
        <div className="flex" style={{ flexDirection: 'column' }}>
          {/* {CustomIcons('no_contact')} */}
          <EmptyState
            minWidth="200px"
            title={`${t('contact_deleted')}`}
            imgSrc="empty-state-response-contact.svg"
          ></EmptyState>
        </div>
      </Center>
    );
  }

  return (
    <>
      <ContactDetailsTable>
        {fields
          .filter(
            (field: FormServFieldType) =>
              SHOW_CONTACT_DETAILS_FIELDS.includes(field.name) ||
              (field.visible &&
                ![
                  ...HIDE_CONTACT_DETAILS_FIELDS,
                  ...TEMPORARY_HIDDEN_FIELDS,
                ].includes(field.name) &&
                !field.deleted),
          )
          .map((field: FormServFieldType) => {
            const fieldValue = getDisplayValueFromField({
              field,
              rowValues: contactData,
              contactLists,
              accountInfo,
            });
            const value = field.custom
              ? get(contactData, ['fields', field.name], '')
              : get(contactData, [field.name], '');
            const fieldId = get(field?.choices?.[value], ['id'], value);
            if (fieldValue) {
              return (
                <ContactDetailField key={field.name}>
                  <div className="key">
                    {i18n.exists(
                      `contacts.import_contact.default_fields.contact.${field.name}`,
                    )
                      ? `${t(
                          `contacts.import_contact.default_fields.contact.${field.name}`,
                        )}`
                      : field.label}
                  </div>
                  <div className="value">
                    {i18n.exists(
                      `contacts.import_contact.form_choices.${field.name}.${fieldId}`,
                    )
                      ? `${t(
                          `contacts.import_contact.form_choices.${field.name}.${fieldId}`,
                        )}`
                      : fieldValue}
                  </div>
                </ContactDetailField>
              );
            } else return <></>;
          })}

        {get(contactData, ['fields', 'list_ids'], []).length > 0 && (
          <ContactDetailField>
            <div className="key">{t('sidebar.contacts.lists')}</div>
            <div className="list">
              {get(contactData, ['fields', 'list_ids'], [])
                .filter(
                  (lId: string) =>
                    !!get(allContactLists, [lId, 'label'], undefined),
                )
                .map((displayId: string) => (
                  <div
                    key={displayId}
                    className="item"
                    onClick={() => history.push(`/lists/${displayId}`)}
                  >
                    {get(allContactLists, [displayId, 'label'], '')}
                  </div>
                ))}
            </div>
          </ContactDetailField>
        )}
      </ContactDetailsTable>
      <FixedContainer>
        {(surveyResponse?.contact_lists || []).length > 0 &&
          !isContactListsError && (
            <ContactDetailField>
              <div className="key">
                {t(
                  'responses.filter_condition.response_fields.responded_from_list',
                )}
              </div>
              <div className="list">
                {(surveyResponse?.contact_lists || []).map(
                  (displayId: string) => (
                    <RespondedFrom key={displayId} id={displayId} type="list" />
                  ),
                )}
              </div>
            </ContactDetailField>
          )}
        {(surveyResponse?.contact_segments || []).length > 0 && (
          // !isSegmentsError && (
          <ContactDetailField>
            <div className="key">
              {t(
                'responses.filter_condition.response_fields.responded_from_segment',
              )}
            </div>
            <div className="list">
              {(surveyResponse?.contact_segments || []).map(
                (displayId: string) => (
                  <RespondedFrom
                    key={displayId}
                    id={displayId}
                    type="segment"
                  />
                ),
              )}
            </div>
          </ContactDetailField>
        )}
      </FixedContainer>
    </>
  );
};

const SentimentDetails: FC<Pick<ContactDisplayProps, 'surveyResponse'>> = ({
  surveyResponse,
}) => {
  const { t } = useTranslation();
  const [isEdit, setIsEdit] = React.useState(false);

  const { freddy_response_analysis } = surveyResponse;

  const positiveSentimentTags =
    freddy_response_analysis?.suggested_tags?.positive || [];
  const neutralSentimentTags =
    freddy_response_analysis?.suggested_tags?.neutral || [];
  const negativeSentimentTags =
    freddy_response_analysis?.suggested_tags?.negative || [];
  const noTopicsPresent =
    positiveSentimentTags?.length === 0 &&
    neutralSentimentTags?.length === 0 &&
    negativeSentimentTags?.length === 0;

  return (
    <>
      <SentimentTagsTable>
        <div className="tag-heading">
          <div className="tag-heading-container">
            <div>{t('responses.freddy_sentiment.overall_sentiment')}</div>{' '}
            <Tooltip
              label={`${t('tooltip.overall_sentiment')}`}
              position="top"
              style={{
                maxWidth: '300px',
              }}
            >
              <Icon width="14" height="14" glyph="info" fill="#465967" />
            </Tooltip>
          </div>
          <Button
            size="mini"
            className="tag-edit-button"
            onClick={() => setIsEdit(true)}
            type="link"
          >
            {t('edit')}
          </Button>
        </div>
        <div
          className={`sentiment-type ${(
            freddy_response_analysis?.customer_sentiment || ''
          ).toLowerCase()}`}
        >
          {t(
            `responses.freddy_sentiment.${freddy_response_analysis?.customer_sentiment.toLowerCase()}`,
          )}
        </div>
        <div className="divider"></div>
        <div className="tag-heading">
          <div className="tag-heading-container">
            <div className="topics-text">
              {t('responses.freddy_sentiment.topics')}
            </div>{' '}
            <Tooltip
              style={{
                maxWidth: '270px',
              }}
              label={`${t('tooltip.topics_sentiment')}`}
              position="bottom-start"
            >
              <Icon width="14" height="14" glyph="info" fill="#465967" />
            </Tooltip>
          </div>
        </div>
        {noTopicsPresent ? (
          <>-</>
        ) : (
          <div className="sentiment-tag-container">
            <SentimentTags
              sentimentTags={positiveSentimentTags}
              sentiment="positive"
            />
            <SentimentTags
              sentimentTags={neutralSentimentTags}
              sentiment="neutral"
            />
            <SentimentTags
              sentimentTags={negativeSentimentTags}
              sentiment="negative"
            />
          </div>
        )}
      </SentimentTagsTable>
      <SentimentTableFooter>
        <div className="sentiment-footer">
          <div className="poweredby-text">
            <Icon width="14" height="14" glyph="copilot" />
            &nbsp;
            <span>
              {' '}
              <Trans
                i18nKey="responses.freddy_sentiment.powered_freddy"
                components={{ bold: <b /> }}
              />
            </span>
            <span className="beta-tag">
              {t('responses.freddy_sentiment.beta')}
            </span>
          </div>
        </div>
      </SentimentTableFooter>
      <Drawer
        isOpen={isEdit}
        onDismiss={() => setIsEdit(false)}
        isFullHeight
        size="medium"
        clickOutToClose={false}
        placement="right"
      >
        <SentimentEdit surveyResponse={surveyResponse} setIsEdit={setIsEdit} />
      </Drawer>
    </>
  );
};

const ResponsesSidebar: FC<ContactDisplayProps> = ({
  surveyResponse,
  accountInfo,
}) => {
  const { t } = useTranslation();
  const { data: tenantInfo } = useGetTenantInfo();

  const isFreddyEnabled = tenantInfo?.account_meta?.enabled_features?.includes(
    'freddy_response_analysis',
  );

  const {
    data: contactForm = {},
    isLoading: isLoadingContactForm,
    isError: isContactFormError,
  } = useGetContactForm({});
  const {
    data: contactData = {},
    isLoading: isLoadingContact,
    isError: isContactError,
    isFetching: isFetchingContact,
  } = useGetContact(surveyResponse?.ucr_contact_id || '', {
    refetchOnWindowFocus: true,
  });
  const {
    data: contactListsResponse = {},
    isLoading: isLoadingContactLists,
    isError: isContactListsError,
  } = useGetContactLists({
    refetchOnWindowFocus: false,
  });

  const isSentimentPresent =
    !!surveyResponse?.freddy_response_analysis?.customer_sentiment &&
    isFreddyEnabled;

  const [selectedTab, setSelectedTab] = React.useState<'sentiment' | 'contact'>(
    isSentimentPresent ? 'sentiment' : 'contact',
  );

  if (
    isLoadingContactForm ||
    isLoadingContactLists ||
    isLoadingContact ||
    isFetchingContact
  ) {
    return (
      <Center>
        <Loader />
      </Center>
    );
  }

  const fields = contactForm?.fields ?? [];

  const isContactPresent =
    fields &&
    fields?.length > 0 &&
    (contactData?.first_name ||
      contactData?.last_name ||
      contactData?.work_email);

  return (
    <>
      <FixedContainer>
        <ContactDetailField>
          <div className="head-title">
            {isSentimentPresent && (
              <DivA11y
                className={
                  selectedTab === 'sentiment' ? 'underline' : 'tab-option'
                }
                onClick={() => setSelectedTab('sentiment')}
              >
                {t('responses.freddy_sentiment.sentiment')}
              </DivA11y>
            )}

            {(isSentimentPresent
              ? isSentimentPresent || isContactPresent
              : isContactPresent) && (
              <DivA11y
                className={
                  selectedTab === 'contact' && isSentimentPresent
                    ? 'underline'
                    : 'tab-option'
                }
                onClick={() => setSelectedTab('contact')}
              >
                {t('contacts.contact_details')}
              </DivA11y>
            )}
          </div>
        </ContactDetailField>
      </FixedContainer>
      {selectedTab === 'sentiment' ? (
        <SentimentDetails surveyResponse={surveyResponse} />
      ) : isContactPresent ? (
        <ContactDetails
          accountInfo={accountInfo}
          surveyResponse={surveyResponse}
          isError={isContactError || isContactFormError}
          contactFields={fields}
          contactLists={contactListsResponse}
          contactData={contactData}
          isContactListsError={isContactListsError}
        />
      ) : (
        <EmptyState
          minWidth="200px"
          title={`${t('empty_state.title.no_contact_data')}`}
          imgSrc="empty-state-response-contact.svg"
        />
      )}
    </>
  );
};

export default ResponsesSidebar;
